import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";
import { toast } from "sonner";
import { LOGO_URL } from "@/data/site";
import { CalendarDays, MapPin, LogOut, CreditCard, FileImage, BellRing, User } from "lucide-react";

const STATUS_STYLE = {
  "In elaborazione": "text-amber-400 border-amber-500/30",
  Confermata: "text-emerald-400 border-emerald-500/30",
  Completata: "text-slate-300 border-white/20",
  Annullata: "text-red-400 border-red-500/30",
};

const TABS = [
  { id: "prenotazioni", label: "Prenotazioni", icon: CalendarDays },
  { id: "report", label: "Report", icon: FileImage },
  { id: "notifiche", label: "Notifiche", icon: BellRing },
];

function mediaUrl(path) {
  const token = localStorage.getItem("adj_token");
  return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`;
}

export default function AreaCliente() {
  const { user, checking, logout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [tab, setTab] = useState("prenotazioni");
  const [bookings, setBookings] = useState([]);
  const [reports, setReports] = useState([]);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const load = useCallback(() => {
    if (!user) return;
    api.get("/bookings/mine").then((r) => setBookings(r.data.bookings)).catch(() => {});
    api.get("/reports/mine").then((r) => setReports(r.data.reports)).catch(() => {});
    api.get("/notifications/mine").then((r) => setNotifications(r.data.notifications)).catch(() => {});
  }, [user]);

  useEffect(load, [load]);

  const cancel = async (id) => {
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success("Prenotazione annullata");
      load();
    } catch {
      toast.error("Impossibile annullare la prenotazione");
    }
  };

  const payNow = async (bookingId) => {
    try {
      const res = await api.post("/payments/checkout", {
        booking_id: bookingId,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Pagamento non disponibile ora."));
    }
  };

  let body;
  if (checking) {
    body = <p className="text-slate-500 text-sm" data-testid="area-loading">Caricamento...</p>;
  } else if (!user) {
    body = (
      <div className="text-center py-20" data-testid="area-login-prompt">
        <User size={40} className="text-orange-500 mx-auto mb-5" />
        <h2 className="font-heading text-2xl font-bold text-white">La tua Area Clienti</h2>
        <p className="text-slate-400 text-sm mt-3 mb-7 max-w-md mx-auto">
          Accedi o registrati per gestire le prenotazioni, vedere i report foto/video dei tuoi
          interventi e ricevere notifiche e promo dedicate.
        </p>
        <button
          onClick={() => setAuthOpen(true)}
          data-testid="area-login-button"
          className="fire-bg text-white font-mono-adj text-xs uppercase tracking-[0.2em] font-semibold rounded-full px-8 py-4 hover:opacity-90 transition-opacity"
        >
          Accedi o Registrati
        </button>
      </div>
    );
  } else {
    body = (
      <>
        <div className="flex flex-wrap gap-2 mb-8 bg-[#0D0E13] border border-white/10 rounded-full p-1.5 w-fit" data-testid="area-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`area-tab-${t.id}`}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-mono-adj text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                tab === t.id ? "fire-bg text-white font-semibold" : "text-slate-400 hover:text-white"
              }`}
            >
              <t.icon size={14} /> {t.label}
              {t.id === "notifiche" && notifications.length > 0 && (
                <span className="bg-orange-500 text-white text-[9px] rounded-full px-1.5 py-0.5 font-bold">{notifications.length}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "prenotazioni" && (
          <div className="flex flex-col gap-4" data-testid="area-bookings-list">
            {bookings.length === 0 && (
              <div className="text-center py-16 bg-[#13141B] border border-white/10 rounded-3xl" data-testid="bookings-empty-state">
                <CalendarDays size={36} className="text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-sm">Nessuna prenotazione ancora.</p>
                <Link to="/#pacchetti" className="text-orange-400 text-sm underline underline-offset-4 mt-2 inline-block">Scopri i piani</Link>
              </div>
            )}
            {bookings.map((b) => (
              <div key={b.id} className="bg-[#13141B] border border-white/10 rounded-2xl p-6" data-testid={`booking-item-${b.id.slice(0, 8)}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="font-display font-bold text-2xl text-white">
                    {b.kw} <span className="text-orange-500">kW</span>
                  </p>
                  <div className="flex gap-2">
                    <span className={`font-mono-adj text-[9px] uppercase tracking-[0.15em] border rounded-full px-2.5 py-1 ${STATUS_STYLE[b.status] || "text-slate-400 border-white/20"}`} data-testid={`booking-status-${b.id.slice(0, 8)}`}>
                      {b.status}
                    </span>
                    <span className={`font-mono-adj text-[9px] uppercase tracking-[0.15em] border rounded-full px-2.5 py-1 ${b.payment_status === "pagato" ? "text-emerald-400 border-emerald-500/30" : "text-slate-400 border-white/15"}`} data-testid={`booking-payment-${b.id.slice(0, 8)}`}>
                      {b.payment_status === "pagato" ? "Pagato" : "Da saldare"}
                    </span>
                  </div>
                </div>
                <p className="font-mono-adj text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-1">{b.plan_label}</p>
                {b.status === "In elaborazione" && (
                  <p className="text-[11px] text-amber-400/90 mt-2" data-testid={`booking-awaiting-note-${b.id.slice(0, 8)}`}>
                    In attesa della nostra conferma: la data dell'intervento ti sarà confermata via email.
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-400">
                  <span className="flex items-center gap-1.5"><CalendarDays size={13} className="text-orange-500" /> {b.date}</span>
                  <span className="flex items-center gap-1.5"><MapPin size={13} className="text-orange-500" /> {b.address}, {b.city}</span>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-white/5">
                  <p className="font-heading font-bold text-white text-lg">€{b.price}</p>
                  <div className="flex items-center gap-4">
                    {b.payment_status !== "pagato" && b.status !== "Annullata" && (
                      <button
                        onClick={() => payNow(b.id)}
                        data-testid={`booking-pay-button-${b.id.slice(0, 8)}`}
                        className="flex items-center gap-2 fire-bg text-white font-mono-adj text-[10px] uppercase tracking-[0.15em] font-semibold rounded-full px-4 py-2.5 hover:opacity-90 transition-opacity"
                      >
                        <CreditCard size={13} /> Paga Ora
                      </button>
                    )}
                    {b.status !== "Annullata" && b.status !== "Completata" && (
                      <button
                        onClick={() => cancel(b.id)}
                        data-testid={`booking-cancel-button-${b.id.slice(0, 8)}`}
                        className="font-mono-adj text-[10px] uppercase tracking-[0.15em] text-red-400/80 hover:text-red-400 transition-colors"
                      >
                        Annulla
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "report" && (
          <div className="flex flex-col gap-5" data-testid="area-reports-list">
            {reports.length === 0 && (
              <div className="text-center py-16 bg-[#13141B] border border-white/10 rounded-3xl" data-testid="reports-empty-state">
                <FileImage size={36} className="text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-sm">Nessun report ancora.</p>
                <p className="text-slate-600 text-xs mt-2">Dopo ogni intervento pubblicheremo qui foto e video della pulizia.</p>
              </div>
            )}
            {reports.map((r) => (
              <div key={r.id} className="bg-[#13141B] border border-white/10 rounded-3xl p-6 sm:p-7" data-testid={`report-item-${r.id.slice(0, 8)}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="font-heading font-bold text-white text-lg">{r.title}</h3>
                  <span className="font-mono-adj text-[10px] uppercase tracking-[0.15em] text-slate-500">
                    {new Date(r.created_at).toLocaleDateString("it-IT")}
                  </span>
                </div>
                {r.note && <p className="text-sm text-slate-400 leading-relaxed mt-2">{r.note}</p>}
                {r.media?.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
                    {r.media.map((m, i) => (
                      <div key={i} className="rounded-2xl overflow-hidden border border-white/10 bg-[#0D0E13] aspect-video" data-testid={`report-media-${r.id.slice(0, 8)}-${i}`}>
                        {m.type === "video" ? (
                          <video controls preload="metadata" src={mediaUrl(m.path)} className="w-full h-full object-cover" />
                        ) : (
                          <img src={mediaUrl(m.path)} alt={m.name || r.title} className="w-full h-full object-cover" loading="lazy" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "notifiche" && (
          <div className="flex flex-col gap-4" data-testid="area-notifications-list">
            {notifications.length === 0 && (
              <div className="text-center py-16 bg-[#13141B] border border-white/10 rounded-3xl" data-testid="notifications-empty-state">
                <BellRing size={36} className="text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-sm">Nessuna notifica al momento.</p>
              </div>
            )}
            {notifications.map((n) => (
              <div key={n.id} className="bg-[#13141B] border border-white/10 rounded-2xl p-6" data-testid={`notification-item-${n.id.slice(0, 8)}`}>
                <div className="flex items-center gap-3">
                  <span className={`font-mono-adj text-[9px] uppercase tracking-[0.2em] border rounded-full px-2.5 py-1 ${n.kind === "promo" ? "text-orange-400 border-orange-500/40" : "text-slate-400 border-white/15"}`}>
                    {n.kind === "promo" ? "Promo" : "Info"}
                  </span>
                  <span className="font-mono-adj text-[10px] text-slate-600">{new Date(n.created_at).toLocaleDateString("it-IT")}</span>
                </div>
                <h3 className="font-heading font-bold text-white mt-3">{n.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed mt-1.5">{n.message}</p>
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="bg-[#050507] min-h-screen" data-testid="area-clienti-page">
      <header className="glass border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="area-logo-link">
            <img src={LOGO_URL} alt="AirDroneJet" className="h-9 w-auto" />
            <span className="font-heading font-extrabold tracking-tight">
              AIR<span className="fire-text">DRONE</span>JET
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="area-back-home-link" className="font-mono-adj text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-orange-400 transition-colors">
              ← Home
            </Link>
            {user && (
              <button
                onClick={() => {
                  logout();
                  toast.success("Sei uscito dall'area clienti");
                }}
                data-testid="area-logout-button"
                className="flex items-center gap-2 font-mono-adj text-[11px] uppercase tracking-[0.15em] text-slate-400 border border-white/15 rounded-full px-4 py-2 hover:border-red-500/50 hover:text-red-400 transition-colors"
              >
                <LogOut size={13} /> Esci
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="font-mono-adj text-xs uppercase tracking-[0.3em] text-orange-500 font-semibold">Area Clienti</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white mt-3 mb-2">
            {user ? <>Ciao, <span className="fire-text">{user.name.split(" ")[0]}</span></> : "Il tuo spazio personale"}
          </h1>
          {user && <p className="text-sm text-slate-500 mb-8">{user.email}</p>}
        </motion.div>
        {body}
      </main>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
}
